import React from 'react';
import { StyleSheet, View } from 'react-native';

import { Button, Modal, Text, useTheme } from '@/components/ui';
import { spacing } from '@/styles/theme';

/**
 * "You have unsaved column changes" confirm shown when the user presses X
 * on the ColumnPicker with a dirty layout. Rendered with the canonical
 * `@/components/ui` Modal so it sits on top of the FlexModal-based picker
 * (the AlertProvider's web dialog portals to the same level as FlexModal
 * and ends up underneath — hence the inline modal).
 */

interface ColumnPickerCloseConfirmProps {
	visible: boolean;
	onCancel: () => void;
	onDiscard: () => void;
	onApply: () => void;
}

export const ColumnPickerCloseConfirm: React.FC<ColumnPickerCloseConfirmProps> = ({
	visible, onCancel, onDiscard, onApply,
}) => {
	const theme = useTheme();
	return (
		<Modal
			visible={visible}
			onDismiss={onCancel}
			contentContainerStyle={styles.modal}
		>
			<Text style={[styles.title, { color: theme.colors.onSurface }]}>
				Apply column changes?
			</Text>
			<Text style={[styles.message, { color: theme.colors.onSurfaceVariant }]}>
				You’ve made changes to which columns are shown. Apply them or discard?
			</Text>
			<View style={styles.actions}>
				<Button mode="text" onPress={onCancel} testID="column-picker-close-cancel">
					Cancel
				</Button>
				<View style={styles.actionsRight}>
					<Button mode="outlined" onPress={onDiscard} testID="column-picker-close-discard">
						Discard
					</Button>
					<Button mode="contained" onPress={onApply} testID="column-picker-close-apply">
						Apply
					</Button>
				</View>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	modal: { width: 440, maxWidth: '95%' },
	title: {
		fontSize: 18,
		fontWeight: '700',
		marginBottom: spacing.s,
	},
	message: {
		fontSize: 14,
		lineHeight: 20,
		marginBottom: spacing.m,
	},
	actions: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: spacing.s,
	},
	actionsRight: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: spacing.s,
	},
});
